import { useEffect, useState } from 'react'

import { api, bundleNameIn } from '../api/client'

/** A minute, plus every time the tab is looked at again. */
const INTERVAL_MS = 60_000

/**
 * The bundle this tab is running.
 *
 * Read from the live document rather than baked in at build time, so it is by
 * definition what is actually executing. Null in dev, where the document points
 * at /src/main.tsx instead of a hashed file.
 */
function runningBundle(): string | null {
  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'))
  for (const script of scripts) {
    // getAttribute, not .src: the property resolves to an absolute URL, which
    // would never match the relative path parsed out of the fetched document.
    const name = bundleNameIn(`<script src="${script.getAttribute('src') ?? ''}"`)
    if (name) return name
  }
  return null
}

/**
 * True once the server is serving a different build than this tab is running.
 *
 * Deliberately one-way: once a new version is known to exist it stays known.
 * A later check that fails, or that races with a deploy still in progress,
 * must not retract the notice and leave someone on a stale bundle believing
 * they are current.
 */
export function useNewVersion(): boolean {
  const [stale, setStale] = useState(false)

  useEffect(() => {
    const running = runningBundle()
    // Nothing to compare in dev, and nothing worth polling for either.
    if (!running) return

    let cancelled = false

    async function check() {
      try {
        const served = await api.servedBundle()
        // A null served value means the check itself failed to answer usefully;
        // treating that as a change would put a permanent bar on the screen the
        // first time the network hiccuped.
        if (!cancelled && served && served !== running) setStale(true)
      } catch {
        // Offline, or a deploy mid-flight. Try again on the next tick.
      }
    }

    void check()
    const timer = setInterval(() => void check(), INTERVAL_MS)
    // The common case is a tab left open overnight: noticed the moment someone
    // comes back to it rather than up to a minute later.
    const onFocus = () => {
      if (document.visibilityState === 'visible') void check()
    }
    document.addEventListener('visibilitychange', onFocus)
    window.addEventListener('focus', onFocus)

    return () => {
      cancelled = true
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onFocus)
      window.removeEventListener('focus', onFocus)
    }
  }, [])

  return stale
}
